import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../../services/api';
import MaterialContent from './MaterialContent';

/**
 * Module Viewer Component
 * Displays module materials and tracks viewing progress
 */
const ModuleViewer = ({ moduleId, onTakeTest, onBack }) => {
  const [module, setModule] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadModule();
  }, [moduleId]);

  const loadModule = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/training/modules/${moduleId}`);

      const moduleData = response.data.module;

      // Parse content if it's a string
      if (moduleData.materials) {
        moduleData.materials = moduleData.materials.map(material => {
          if (typeof material.content === 'string') {
            try {
              material.content = JSON.parse(material.content);
            } catch (e) {
              console.error('Failed to parse material content:', e);
            }
          }
          return material;
        });
      }

      console.log('Module loaded:', moduleData);
      console.log('First material:', moduleData.materials?.[0]);

      setModule(moduleData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading module:', err);
      setError('Не удалось загрузить модуль');
      setLoading(false);
    }
  };

  const handleMarkMaterialViewed = async (materialId) => {
    try {
      await api.post(`/api/training/modules/${moduleId}/materials/${materialId}/viewed`);

      // Reload module to update progress
      await loadModule();
    } catch (err) {
      console.error('Error marking material as viewed:', err);
    }
  };

  const handleMaterialClick = async (material) => {
    console.log('Material clicked:', material);
    console.log('Material content type:', typeof material.content);
    console.log('Material content:', material.content);

    setSelectedMaterial(material);

    // Mark as viewed if not already
    if (module?.progress && !module.progress.materials_viewed?.includes(material.id)) {
      await handleMarkMaterialViewed(material.id);
    }
  };

  const isMaterialViewed = (materialId) => {
    return module?.progress?.materials_viewed?.includes(materialId) || false;
  };

  const getViewedCount = () => {
    return module?.progress?.materials_viewed?.length || 0;
  };

  const getTotalMaterials = () => {
    return module?.materials?.length || 0;
  };

  const canTakeTest = () => {
    const required = module?.materials?.filter((m) => m.is_required) || [];
    const viewed = module?.progress?.materials_viewed || [];
    return required.every((m) => viewed.includes(m.id));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка модуля...</p>
        </div>
      </div>
    );
  }

  if (error || !module) {
    return (
      <div className="text-center p-8">
        <p className="text-red-600">{error || 'Модуль не найден'}</p>
        <button
          onClick={onBack}
          className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Назад
        </button>
      </div>
    );
  }

  const viewedCount = getViewedCount();
  const totalMaterials = getTotalMaterials();
  const progressPercentage = totalMaterials > 0 ? Math.round((viewedCount / totalMaterials) * 100) : 0;

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Module Header */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <button
          onClick={onBack}
          className="text-blue-600 hover:text-blue-700 mb-4 flex items-center"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Назад к модулям
        </button>

        <h1 className="text-3xl font-bold text-gray-800 mb-2">{module.title}</h1>
        <p className="text-gray-600 mb-4">{module.description}</p>

        {/* Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Прогресс изучения</span>
            <span>{viewedCount} из {totalMaterials} материалов</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {canTakeTest() && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-green-800">Все материалы изучены!</p>
                <p className="text-sm text-green-600">Теперь вы можете пройти тест по модулю</p>
              </div>
              <button
                onClick={onTakeTest}
                className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 font-semibold"
              >
                Пройти тест
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Materials List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Материалы модуля</h2>

            <div className="space-y-2">
              {module.materials && module.materials.length > 0 ? (
                module.materials.map((material, index) => {
                  const viewed = isMaterialViewed(material.id);

                  return (
                    <button
                      key={material.id}
                      onClick={() => handleMaterialClick(material)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        selectedMaterial?.id === material.id
                          ? 'border-blue-500 bg-blue-50'
                          : viewed
                          ? 'border-green-200 bg-green-50 hover:bg-green-100'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start">
                        <span className="flex-shrink-0 w-6 h-6 mr-3 mt-0.5">
                          {viewed ? (
                            <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                          )}
                        </span>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-800 text-sm">
                            {index + 1}. {material.title}
                          </p>
                          {material.is_required && (
                            <span className="text-xs text-orange-600">Обязательно</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              ) : (
                <p className="text-gray-500 text-center py-4">Нет материалов</p>
              )}
            </div>
          </div>
        </div>

        {/* Material Content */}
        <div className="lg:col-span-2">
          {selectedMaterial ? (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">{selectedMaterial.title}</h2>
                {selectedMaterial.category && (
                  <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                    {selectedMaterial.category}
                  </span>
                )}
              </div>

              <div className="prose max-w-none">
                <MaterialContent content={selectedMaterial.content} />
              </div>

              {selectedMaterial.examples && selectedMaterial.examples.length > 0 && (
                <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                  <h3 className="font-semibold text-gray-800 mb-2">Примеры:</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    {selectedMaterial.examples.map((example, index) => (
                      <li key={index}>{example}</li>
                    ))}
                  </ul>
                </div>
              )}

              {!isMaterialViewed(selectedMaterial.id) && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    Материал автоматически отмечен как просмотренный
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center">
              <svg className="w-24 h-24 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500 text-lg">Выберите материал для изучения</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

ModuleViewer.propTypes = {
  moduleId: PropTypes.number.isRequired,
  onTakeTest: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default ModuleViewer;
